import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import produce from "immer";

import "./LocationGroups.scss";
import { ErrorBoundary, ErrorMessage, Loader, Page, PageHeader } from "components";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, IconButton} from '@cfa/react-components';
import { AsyncStatus } from "enums";
import { ILocationGroup } from "interfaces";
import { LocationGroupsService, parseAxiosError } from "services";
import { LocationGroupModal } from "./LocationGroupModal";

interface ILocationGroupPageState {
    loadStatus: AsyncStatus;
    loadError?: string;
    deleteStatus?: AsyncStatus;
    deleteError?: string;
    locationGroups?: ILocationGroup[];
    editing?: boolean;
    editLocationGroup?: ILocationGroup;
    deleteLocationGroup?: ILocationGroup;
}

export function LocationGroups() {

    const [pageState, setPageState] = useState<ILocationGroupPageState>({
        loadStatus: AsyncStatus.Loading,
        deleteStatus: AsyncStatus.Initial,
        locationGroups: [],
    });
    const { loadStatus, loadError, deleteStatus, deleteError, locationGroups, editing, editLocationGroup, deleteLocationGroup } = pageState;
    
    // create modal control
    const [createShow, setCreateShow] = useState(false);
    const showCreateModal = () => {
        setCreateShow(true);
    };

    const showEditModal = (group) => {
        setPageState(
            produce((draft) => {
                draft.editLocationGroup = group;
                draft.editing = true;
            })
        );
        setCreateShow(true);
    };

    const closeEditModal = () => {
        setPageState(
            produce((draft) => {
                draft.editLocationGroup = undefined;
                draft.editing = false;
            })
        );
        setCreateShow(false);
    };

    // delete modal control
    const [deleteShow, setDeleteShow] = useState(false);
    const handleDeleteClose = () => setDeleteShow(false);
    const showDeleteModal = (group) => {
        setPageState(
            produce((draft) => {
                draft.deleteLocationGroup = group;
            })
        );
        setDeleteShow(true);
    };

    // data is preloaded in app.tsx, so just assign from service
    useEffect(() => {
        if (loadStatus === AsyncStatus.Success) return;

        setPageState(
            produce((draft) => {
                draft.loadStatus = AsyncStatus.Success;
                draft.locationGroups = LocationGroupsService.locationGroups;
            })
        );
    });
 
    // soft deletes the LocationGroup in the DB
    const onDeleteLocationGroup = (group: ILocationGroup) => {
        setPageState(
            produce((draft) => {
                draft.deleteLocationGroup = group;
                draft.deleteError = '';
                draft.deleteStatus = AsyncStatus.Loading;
            })
        )
        LocationGroupsService.deleteLocationGroup(group.id)
            .then(() => {
                setPageState(
                    produce((draft) => {
                        draft.deleteLocationGroup = undefined;
                        draft.deleteStatus = AsyncStatus.Success;
                        draft.locationGroups = LocationGroupsService.locationGroups;
                    })
                )
                setDeleteShow(false);
            })
            .catch((err) => {
                setPageState(
                    produce((draft) => {
                        draft.deleteError = parseAxiosError(err);
                        draft.deleteStatus = AsyncStatus.Error;
                    })
                );
            });
        
    }

    return (<>
        <Page>
            <PageHeader title="Location Groups" right={<Button onClick={showCreateModal}>{'New Location Group'}</Button>}/>

            <div className="row locationGroups_colHeaders">
                <div className="locationGroups_colHeader locationGroups_colName">Name</div>
                <div className="locationGroups_colHeader locationGroups_colLocations">Locations</div>
            </div>
            
            <div className="locationGroups_list">
                <ErrorBoundary>
                    {loadStatus === AsyncStatus.Loading && <Loader />}
                    {loadStatus === AsyncStatus.Success &&
                        locationGroups.length > 0 && locationGroups.map((group) => {
                            return (
                                <div className="locationGroups_listItem" key={group.id}>
                                    <div className="locationGroups_colName">{group.name}</div>
                                    <div className="locationGroups_colLocations" style={{paddingLeft: 20}}>
                                        {group.locations.length > 10 
                                            ? group.locations.slice(0, 10).join(", ") + "..." 
                                            : group.locations.join(", ")}
                                    </div>
                                    <div>
                                        <IconButton
                                            size="sm"
                                            onClick={() => showEditModal(group)}
                                        >
                                            <FontAwesomeIcon icon="edit" />
                                        </IconButton>
                                    </div>
                                    <div>
                                        <IconButton
                                            size="sm"
                                            onClick={() => showDeleteModal(group)}
                                        >
                                            <FontAwesomeIcon icon="trash" />
                                        </IconButton>
                                    </div>
                                </div>
                            );
                        })}
                    {loadStatus === AsyncStatus.Success &&
                        locationGroups.length === 0 && (
                            <div className="locationGroups_listEmpty">
                                <FontAwesomeIcon
                                    icon="search"
                                    className="locationGroups_listEmptyIcon"
                                />
                                <div className="locationGroups_listEmptyText">
                                    No location groups found.
                                </div>
                            </div>
                        )}
                    {loadStatus === AsyncStatus.Error && (
                        <ErrorMessage message={loadError} />
                    )}
                </ErrorBoundary>
            </div>
        </Page>

        <Modal show={deleteShow} onClose={handleDeleteClose}>
            <ModalHeader>
                Delete Location Group
            </ModalHeader>
            <ModalBody>
                <p>Are you sure you want to delete this locationGroup?</p>
                <div className="locationGroups_deleteHeader">
                    Name: {deleteLocationGroup && deleteLocationGroup.name}<br/>
                    ID: {deleteLocationGroup && deleteLocationGroup.id}
                </div>
            </ModalBody>
            <ModalFooter>
                {deleteStatus === AsyncStatus.Error && (
                    <ErrorMessage message={deleteError} />
                )}
                <Button variant="outlined" disabled={deleteStatus === AsyncStatus.Loading} onClick={handleDeleteClose}>
                    Cancel
                </Button>
                <Button loading={deleteStatus === AsyncStatus.Loading} onClick={() => onDeleteLocationGroup(deleteLocationGroup)}>
                    Delete
                </Button>
            </ModalFooter>
        </Modal>

        <Modal show={createShow} onClose={closeEditModal}>
            <LocationGroupModal locationGroup={editLocationGroup} editing={editing} onHide={closeEditModal}/>
        </Modal>
    </>);
}
